import React from "react";
import {
  DealThreadMessageRole,
  DealThreadMessageStatus,
  useCreateDealThreadMutation,
  useCreateDealThreadMessageMutation,
  DealThreadDocument,
  DealThreadQuery,
  DealThreadQueryVariables,
  DealThreadMessageContentQuoteStatus,
  DealThreadMessageQuery,
  DealThreadMessageQueryVariables,
  DealThreadMessageDocument,
  DealThreadMessage,
  useDealQuery,
  useDealThreadsQuery,
  DealThread,
  DealThreadMessageSourceType,
  DealThreadMessageContentBlock,
  DealThreadMessageContentBlockType
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useDispatch, useSelector } from "react-redux";
import { SlideOver } from "../../SlideOver";
import { assistantSelectors } from "@/src/store/assistant/selector";
import {
  BotIcon,
  CheckCircleIcon,
  FileWarningIcon, PlusIcon, ChevronDownIcon,
  ChevronUpIcon,
  ExternalLinkIcon,
  ChevronRightIcon, PanelRightClose
} from "lucide-react";
import { Button } from "../../tailwind/Button";
import { toasts } from "../../toasts/toasts";
import { AppState } from "@/src/store";
import { actions } from "@/src/store/assistant/slice";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  DealThreadMessageContentQuote,
  DealThreadMessageCreatedData,
  useWebSocket,
} from "@/src/contexts/websockets";
import { classNames } from "@/src/utils/cn";
import { animated, useSpring } from "react-spring";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { FilePill } from "../../FilePill";
import { Pills } from "../../Pills";
import { v4 as uuid } from "uuid";
import { Spinner } from "../../icons/Spinner";
import { PreviousChats } from "./previous_chats";
import { getUnixTime } from "date-fns";
import { useManualRefresh } from "@/src/hooks/useManualRefresh";
import { gqlDealThreadMessageToWebsocketDealThreadMessage } from "@/src/utils/typeconv";
import { NavLink, useParams } from "react-router-dom";
import { QuestionPill } from "@/src/routes/data_room/QuestionPill";
import { useQueryClient } from "@tanstack/react-query";
import { ExcelSnippet } from "../../ExcelSnippet";
import { FileIcon } from "../../FileIcon";
import { links } from "@/src/utils/links";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AnswerViewer } from "@/src/components/answer_editor/AnswerEditor";
import { AiMessageReasoningBlock } from "@/src/components/ai/reasoning";
import {
  ToolCallGroup,
  groupBlocks,
  type AiBlockFields,
} from "@/src/components/ai/tool-calls";

function PillsWithShowMore(props: { children: React.ReactNode }) {
  const [showAll, setShowAll] = useState(false);
  const childrenArray = React.Children.toArray(props.children);

  if (childrenArray.length <= 3) {
    return <Pills>{props.children}</Pills>;
  }

  const visibleChildren = showAll ? childrenArray : childrenArray.slice(0, 3);
  const hiddenCount = childrenArray.length - 3;

  return (
    <div className="space-y-2">
      <Pills>{visibleChildren}</Pills>
      <button
        onClick={() => setShowAll(!showAll)}
        className="flex items-center gap-x-1 text-xs text-gray-500 hover:text-gray-700 font-medium"
      >
        {showAll ? (
          <>
            <ChevronUpIcon className="w-3 h-3" />
            Show less
          </>
        ) : (
          <>
            <ChevronDownIcon className="w-3 h-3" />
            Show {hiddenCount} more source{hiddenCount > 1 ? "s" : ""}
          </>
        )}
      </button>
    </div>
  );
}

export function Assistant(props: { onClose: () => void; open: boolean }) {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const createDealThread = useCreateDealThreadMutation(client);
  const dispatch = useDispatch();

  const previousDealIdRef = useRef<string | undefined>(activeDealId);
  useEffect(() => {
    if (
      previousDealIdRef.current &&
      previousDealIdRef.current !== activeDealId
    ) {
      dispatch(actions.clearAssistantState());
    }
    previousDealIdRef.current = activeDealId;
  }, [activeDealId, dispatch]);

  const dealThreads = useDealThreadsQuery(client, {
    dealId: activeDealId,
  });

  const activeDealQuery = useDealQuery(client, {
    id: activeDealId,
  });

  const assistantDisplayMode = useSelector(
    assistantSelectors.assistantDisplayMode,
  );

  if (activeDealQuery.isPending || !activeDealQuery.data) {
    return null;
  }

  const activeDeal = activeDealQuery.data.deal;

  function createThread() {
    if (!activeDealId) return;
    createDealThread.mutate(
      {
        input: {
          dealID: activeDealId,
        },
      },
      {
        onError: () => {
          toasts.error("Failed to create chat");
        },
        onSuccess: (data) => {
          console.log(
            "New chat created, setting active thread and adding messages",
            data.createDealThread.id,
          );
          dispatch(actions.setActiveDealThreadId(data.createDealThread.id));
          queryClient.invalidateQueries({
            queryKey: ["DealThreads", { dealId: activeDealId }],
          });

          for (const message of data.createDealThread.messages) {
            const payload = {
              dealThreadId: data.createDealThread.id,
              message: {
                dealThreadID: data.createDealThread.id,
                dealThreadMessageID: message.id,
                dealThreadMessage: {
                  id: message.id,
                  status: message.status,
                  role: message.role,
                  sources: {
                    sources: [],
                  },
                  content: {
                    type: "text",
                    text: {
                      value: message.content.text.value,
                    },
                    quotes: [],
                    files: [],
                  },

                  createdAt: message.createdAt,
                },
              },
            };
            console.log("Adding message from new chat", payload);
            dispatch(actions.addDealThreadMessage(payload));
          }
        },
      },
    );
  }

  const assistantContent = (
    <div className="flex h-full min-h-0 flex-col overflow-hidden pt-3 bg-concrete-50">
      <div className="px-1 sm:px-3 shadow-xl py-1.5 pb-2  ">
        <div className="flex items-start  justify-between">
          <div className="flex items-start gap-x-2">
            <Button
              text=""
              icon={PanelRightClose}
              size="s"
              variant="neutral/soft"
              onClick={() => props.onClose()}
            />
            <div className="">
              <p className="leading-none font-semibold text-concrete-800 text-md">
                Deal Assistant
              </p>
              <p className="text-sm text-concrete-600">
                {activeDeal ? activeDeal.company.name : ""}
              </p>
            </div>
          </div>
          <div className="ml-3 flex h-7 gap-x-2 items-center">
            {/* <Button
              variant="positive"
              text=""
              icon={
                assistantDisplayMode === "slideover"
                  ? PanelRightIcon
                  : ExternalLinkIcon
              }
              size="s"
              onClick={() => {
                dispatch(
                  actions.setAssistantDisplayMode(
                    assistantDisplayMode === "slideover" ? "panel" : "slideover",
                  ),
                );
              }}
            /> */}

            <PreviousChats />
            <Button
              icon={PlusIcon}
              variant="neutral/soft"
              text="New chat"
              size="s"
              onClick={() => {
                createThread();
              }}
            />
          </div>
        </div>
      </div>
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {dealThreads.data && dealThreads.data.dealThreads.length > 0 ? (
          <Chat dealThreads={dealThreads.data.dealThreads as DealThread[]} />
        ) : (
          <div className="flex bg-white flex-col items-center justify-center flex-1 h-full w-full p-8 text-center">
            <BotIcon className="w-6 h-6 text-gray-700" />
            <p className="font-semibold text-gray-700 text-sm">
              You haven't used the Assistant in this deal yet, click below to
              start.
            </p>

            <Button
              text="New Chat"
              icon={PlusIcon}
              margin="m 0 0 0"
              onClick={() => {
                createThread();
              }}
              variant="neutral"
            />
          </div>
        )}
      </div>
    </div>
  );

  if (assistantDisplayMode === "panel") {
    return (
      <>
        <div
          className={classNames(
            "hidden lg:block self-start sticky top-0 h-screen border-l border-gray-200 bg-white overflow-hidden transition-all duration-300 ease-out",
            props.open ? "min-w-[28rem] w-[28rem]" : "min-w-0 w-0",
          )}
        >
          {props.open ? <div className="h-full w-[28rem]">{assistantContent}</div> : null}
        </div>
        <div className="lg:hidden">
          <SlideOver
            withOutsideClick={false}
            withBackground={false}
            onClose={props.onClose}
            open={props.open}
          >
            {assistantContent}
          </SlideOver>
        </div>
      </>
    );
  }

  return (
    <SlideOver
      withOutsideClick={false}
      withBackground={false}
      onClose={props.onClose}
      open={props.open}
    >
      {assistantContent}
    </SlideOver>
  );
}

let scrollTimeout: NodeJS.Timeout | null = null;

function Chat(props: { dealThreads: DealThread[] }) {
  const [newMessage, setNewMessage] = useState("");

  const { subscribeToTopic, unsubscribeFromTopic } = useWebSocket();
  const dispatch = useDispatch();

  const client = useGqlClient();
  const activeDealThreadId = useSelector(assistantSelectors.activeDealThreadId);

  const createDealThreadMessage = useCreateDealThreadMessageMutation(client);

  const dealThreadMessages = useSelector((state: AppState) =>
    assistantSelectors.dealThreadMessages(state, activeDealThreadId ?? ""),
  );

  const lastMessage =
    dealThreadMessages && dealThreadMessages.length > 0
      ? dealThreadMessages[dealThreadMessages.length - 1]
      : undefined;
  const [chatDisabled, setChatDisabled] = useState(false);

  useEffect(() => {
    const isActiveThreadInCurrentDeal =
      activeDealThreadId &&
      props.dealThreads.some((t) => t.id === activeDealThreadId);

    if (!isActiveThreadInCurrentDeal && props.dealThreads.length > 0) {
      dispatch(actions.setActiveDealThreadId(props.dealThreads[0].id));
    }
  }, [activeDealThreadId, props.dealThreads, dispatch]);

  useEffect(() => {
    if (!lastMessage) {
      return;
    }

    if (
      lastMessage.dealThreadMessage.role === DealThreadMessageRole.User &&
      (lastMessage.dealThreadMessage.status ===
        DealThreadMessageStatus.Sending ||
        lastMessage.dealThreadMessage.status === DealThreadMessageStatus.Sent)
    ) {
      //   setChatDisabled(true);
      return;
    }

    setChatDisabled(false);
    textAreaRef?.current?.focus();
  }, [
    lastMessage,
    lastMessage?.dealThreadMessage.status,
    lastMessage?.dealThreadMessage.role,
  ]);

  useEffect(() => {
    if (!activeDealThreadId) {
      return;
    }

    // Check if we already have messages for this thread to avoid re-fetching
    const existingMessages = dealThreadMessages;
    if (existingMessages && existingMessages.length > 0) {
      console.log(
        "Thread messages already exist, skipping fetch",
        activeDealThreadId,
      );
      return;
    }

    client
      .request<DealThreadQuery, DealThreadQueryVariables>(DealThreadDocument, {
        id: activeDealThreadId,
      })
      .then((res) => {
        console.log("Fetched deal thread, adding messages", res.dealThread.id);
        res.dealThread.messages.forEach((message) => {
          const payload = {
            dealThreadId: activeDealThreadId,
            message: gqlDealThreadMessageToWebsocketDealThreadMessage(
              message as DealThreadMessage,
              activeDealThreadId,
            ),
          };
          console.log("Adding message from fetched thread", payload);
          dispatch(actions.addDealThreadMessage(payload));
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDealThreadId]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const createCommentRef = useRef<HTMLButtonElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }

    scrollTimeout = setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, [dealThreadMessages]);

  useEffect(
    () => {
      if (!activeDealThreadId) {
        return;
      }

      subscribeToTopic(`deal_thread:${activeDealThreadId}`);

      return () => {
        unsubscribeFromTopic(`deal_thread:${activeDealThreadId}`);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeDealThreadId],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (createCommentRef.current) {
        createCommentRef.current.click();
      }
    }
  };

  const [seenMessageIds, setSeenMessageIds] = useState(new Set());

  useEffect(() => {
    if (!dealThreadMessages) return;

    const newIds = dealThreadMessages
      .map((msg) => msg.dealThreadMessage.id)
      .filter((id) => !seenMessageIds.has(id));

    if (newIds.length > 0) {
      setTimeout(() => {
        setSeenMessageIds((prev) => {
          const updated = new Set(prev);
          newIds.forEach((id) => updated.add(id));
          return updated;
        });
      }, 500);
    }
  }, [dealThreadMessages, seenMessageIds]);

  function cslx(arg0: boolean): string | undefined {
    throw new Error("Function not implemented.");
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-scroll overscroll-contain bg-concrete-50">
        {dealThreadMessages &&
          dealThreadMessages.map((message) => {
            if (!message.dealThreadMessage.content) {
              return null;
            }

            const isNew = !seenMessageIds.has(message.dealThreadMessage.id);

            return (
              <AnimatedMessage
                key={message.dealThreadMessage.id}
                message={message}
                isNew={isNew}
              />
            );
          })}
        <div ref={scrollRef} />
      </div>

      <div className="p-4 border-t bg-gray-50">
        <div className="relative flex-auto">
          <div
            className={classNames(
              chatDisabled ? "opacity-50 focus-within:ring-gray-300" : "",
              "bg-white overflow-hidden rounded-lg pb-12  ring-1 ring-inset ring-concrete-200 focus-within:ring-1 focus-within:ring-concrete-800 focus-within:outline outline-concrete-200",
            )}
          >
            <label htmlFor="comment" className="sr-only">
              Add your message
            </label>
            <textarea
              ref={textAreaRef}
              rows={2}
              name="comment"
              value={newMessage}
              onKeyDown={(e) => {
                handleKeyDown(e);
              }}
              id="comment"
              onChange={(e) => {
                if (chatDisabled) {
                  return;
                }

                setNewMessage(e.currentTarget.value);
              }}
              className={classNames(
                chatDisabled ? "opacity-50" : "",
                "p-4 focus:outline-none block w-full resize-none border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6",
              )}
              placeholder="Send a message..."
            />
          </div>

          <div className="absolute inset-x-0 bottom-0 flex justify-between py-2 pl-3 pr-2">
            <div className="flex items-center space-x-5"></div>
            <Button
              isDisabled={chatDisabled || newMessage.length === 0}
              text="Send"
              variant="neutral"
              size="s"
              ref={createCommentRef}
              onClick={() => {
                if (!newMessage) {
                  return;
                }

                const newId = `deal_thread_msg_${uuid()}`;
                const messageToAdd: DealThreadMessageCreatedData = {
                  dealThreadID: activeDealThreadId ?? "",
                  dealThreadMessageID: newId,
                  dealThreadMessage: {
                    id: newId,
                    role: DealThreadMessageRole.User,
                    status: DealThreadMessageStatus.Sending,
                    sources: {
                      sources: [],
                    },
                    content: {
                      text: {
                        value: newMessage,
                      },
                      quotes: [],
                      files: [],
                      type: "text",
                    },
                    createdAt: getUnixTime(new Date()),
                  },
                };

                const payload = {
                  dealThreadId: activeDealThreadId ?? "",
                  message: messageToAdd as DealThreadMessageCreatedData,
                };
                console.log("Adding new user message (optimistic)", payload);
                dispatch(actions.addDealThreadMessage(payload));

                setNewMessage("");

                // Optimistically add AI response with "thinking" status
                const optimisticAiMessageId = `deal_thread_msg_ai_${uuid()}`;
                const aiMessageToAdd: DealThreadMessageCreatedData = {
                  dealThreadID: activeDealThreadId ?? "",
                  dealThreadMessageID: optimisticAiMessageId,
                  dealThreadMessage: {
                    id: optimisticAiMessageId,
                    role: DealThreadMessageRole.Ai,
                    status: DealThreadMessageStatus.Thinking,
                    sources: {
                      sources: [],
                    },
                    content: {
                      text: {
                        value: "",
                      },
                      quotes: [],
                      files: [],
                      type: "text",
                    },
                    createdAt: getUnixTime(new Date()),
                  },
                };

                const aiPayload = {
                  dealThreadId: activeDealThreadId ?? "",
                  message: aiMessageToAdd as DealThreadMessageCreatedData,
                };
                console.log("Adding optimistic AI thinking message", aiPayload);

                // Add a delay to make the AI thinking appear more natural
                const aiMessageTimeout = setTimeout(() => {
                  dispatch(actions.addDealThreadMessage(aiPayload));
                }, 1000);

                createDealThreadMessage.mutate(
                  {
                    input: {
                      id: newId,
                      message: newMessage,
                      dealThreadID: activeDealThreadId ?? "",
                    },
                  },
                  {
                    onError: (e) => {
                      // Clear the timeout to prevent adding AI message if request failed
                      clearTimeout(aiMessageTimeout);

                      dispatch(
                        actions.removeDealThreadMessage({
                          dealThreadId: activeDealThreadId ?? "",
                          messageId: optimisticAiMessageId,
                        }),
                      );

                      const payload = {
                        dealThreadId: activeDealThreadId ?? "",
                        messageId: newId,
                        status: DealThreadMessageStatus.Failed,
                      };
                      console.log(
                        "Failed to send message, updating status",
                        payload,
                      );
                      dispatch(actions.updateDealThreadMessageStatus(payload));
                    },
                    onSuccess: (data) => {
                      // Clear the timeout and remove the optimistic AI message on success
                      clearTimeout(aiMessageTimeout);
                      dispatch(
                        actions.removeDealThreadMessage({
                          dealThreadId: activeDealThreadId ?? "",
                          messageId: optimisticAiMessageId,
                        }),
                      );

                      const statusUpdatePayload = {
                        dealThreadId: activeDealThreadId ?? "",
                        messageId: newId,
                        status: DealThreadMessageStatus.Sent,
                      };
                      console.log(
                        "Successfully sent message, updating status",
                        statusUpdatePayload,
                      );
                      dispatch(
                        actions.updateDealThreadMessageStatus(
                          statusUpdatePayload,
                        ),
                      );

                      const addMessagePayload = {
                        dealThreadId: activeDealThreadId ?? "",
                        message: {
                          dealThreadID: activeDealThreadId ?? "",
                          dealThreadMessageID:
                            data.createDealThreadMessage.responseMessage.id,
                          dealThreadMessage: {
                            id: data.createDealThreadMessage.responseMessage.id,
                            sources: {
                              sources: [],
                            },
                            status:
                              data.createDealThreadMessage.responseMessage
                                .status,
                            role: data.createDealThreadMessage.responseMessage
                              .role,

                            content: {
                              type: "text",
                              text: {
                                value: "",
                              },
                              quotes: [],
                              files: [],
                            },
                            createdAt:
                              data.createDealThreadMessage.responseMessage
                                .createdAt,
                          },
                        },
                      };
                      console.log(
                        "Adding assistant message placeholder",
                        addMessagePayload,
                      );
                      dispatch(actions.addDealThreadMessage(addMessagePayload));
                    },
                  },
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function AnimatedMessage(props: {
  message: DealThreadMessageCreatedData;
  isNew: boolean;
}) {
  const message = props.message;
  const style = useSpring({
    from: props.isNew
      ? { transform: "translate3d(0,40px,0)", opacity: 0 }
      : { transform: "translate3d(0,0px,0)", opacity: 1 },
    to: { transform: "translate3d(0,0px,0)", opacity: 1 },
    config: { tension: 125, friction: 20, precision: 0.1 },
    immediate: props.isNew === false,
  });

  if (!message.dealThreadMessage.content) {
    return null;
  }
  if (message.dealThreadMessage.role === DealThreadMessageRole.User) {
    return (
      <animated.div
        style={style}
        key={message.dealThreadMessage.id}
        className={classNames(
          "flex items-center space-x-2 w-full",
          message.dealThreadMessage.status ===
            DealThreadMessageStatus.Sending ||
            message.dealThreadMessage.status === DealThreadMessageStatus.Failed
            ? "opacity-50"
            : "opacity-1",
        )}
      >
        <div className="bg-white border rounded-md border-concrete-200 p-2 mx-2 w-full">
          <p className="text-sm text-gray-600">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
              {message.dealThreadMessage.content.text.value}
            </ReactMarkdown>
          </p>
        </div>
      </animated.div>
    );
  }

  return <AnimatedAiMessage message={message} isNew={props.isNew} />;
}

function AnimatedAiMessage(props: {
  message: DealThreadMessageCreatedData;
  isNew: boolean;
}) {
  const message = props.message;
  const dispatch = useDispatch();
  const style = useSpring({
    from: props.isNew
      ? { transform: "translate3d(0,40px,0)", opacity: 0 }
      : { transform: "translate3d(0,0px,0)", opacity: 1 },
    to: { transform: "translate3d(0,0px,0)", opacity: 1 },
    config: { tension: 125, friction: 20, precision: 0.1 },
    immediate: props.isNew === false,
  });

  const client = useGqlClient();

  const { dealId } = useParams<{ dealId: string }>();

  const refreshCallback = useCallback(
    () => {
      if (!props.message.dealThreadMessage.id) {
        return;
      }

      client
        .request<DealThreadMessageQuery, DealThreadMessageQueryVariables>(
          DealThreadMessageDocument,
          {
            id: props.message.dealThreadMessage.id,
          },
        )
        .then((res) => {
          console.log("res from refreshCallback", res.dealThreadMessage);
          const payload = {
            dealThreadId: props.message.dealThreadID,
            messageId: props.message.dealThreadMessageID,
            message: gqlDealThreadMessageToWebsocketDealThreadMessage(
              res.dealThreadMessage as DealThreadMessage,
              props.message.dealThreadID,
            ),
          };
          console.log("Manually refreshing message", payload);
          dispatch(actions.updateDealThreadMessage(payload));
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      client,
      props.message.dealThreadMessage.id,
      props.message.dealThreadID,
      props.message.dealThreadMessageID,
    ],
  );

  const quotes = message.dealThreadMessage.content.quotes;

  // Check if any quotes are still verifying
  const hasVerifyingQuotes =
    quotes && quotes.some(
      (quote) => quote.status === DealThreadMessageContentQuoteStatus.Verifying,
    ) ? true : false;

  const allQuotesVerifying = quotes && quotes.every(
    (quote) => quote.status === DealThreadMessageContentQuoteStatus.Verifying,
  );

  useManualRefresh(
    {
      messageCreatedAt: props.message.dealThreadMessage.createdAt,
      messageLastStreamedAt: props.message.lastStreamedMessageAt,
      messageStatus: props.message.dealThreadMessage.status,
      hasVerifyingQuotes,
    },
    refreshCallback,
  );

  const groupedQuotes = quotes && quotes.reduce(
    (groups, quote) => {
      const fileId = quote.dataRoomFileId || quote.questionId || "no-key";
      if (!groups[fileId]) {
        groups[fileId] = [];
      }
      groups[fileId].push(quote);
      return groups;
    },
    {} as Record<string, typeof quotes>,
  );

  return (
    <animated.div
      style={style}
      key={message.dealThreadMessage.id}
      className="flex w-full min-w-0 items-end space-x-2 mb-4"
    >
      <div className="w-full min-w-0 rounded-md py-2 px-4">
        {/* {message.dealThreadMessage.sources ? (
          <AiMessageSources message={message} />
        ) : null} */}
        <AiMessageContent message={message} />
        <div className="mt-2">
          {message.dealThreadMessage.content.quotes &&
            message.dealThreadMessage.content.quotes.length > 0
            ? (() => {
              if (allQuotesVerifying) {
                // Render all quotes in a single box when all are verifying
                return (
                  <div className="p-3 bg-white border my-1 border-gray-200 rounded-md space-y-2">
                    <div className="flex items-center">
                      <Spinner color="gray" size="s" />
                      <p className="text-sm text-gray-600 font-semibold">
                        Verifying quotes...
                      </p>
                    </div>
                    <Pills>
                      {quotes.map((quote, i) => (
                        <React.Fragment
                          key={`${quote.dataRoomFileId}-${quote.pageIndex}-${i}`}
                        >
                          {quote.dataRoomFileId ? (
                            <FilePill
                              id={quote.dataRoomFileId}
                              name={quote.dataRoomFileName}
                              type={quote.dataRoomFileType}
                              pageIndex={quote.pageIndex}
                              showDetailsCard={false}
                              rectsOnPage={
                                quote.rectsOnPage
                                  ? quote.rectsOnPage
                                  : undefined
                              }
                            />
                          ) : null}
                          {quote.questionId ? (
                            <QuestionPill
                              questionId={quote.questionId}
                              style="assistant"
                            />
                          ) : null}
                        </React.Fragment>
                      ))}
                    </Pills>
                  </div>
                );
              } else {
                return Object.entries(groupedQuotes).map(
                  ([fileId, fileQuotes]) => {
                    return (
                      <AiMessageQuoteGroup
                        fileId={fileId}
                        fileQuotes={fileQuotes}
                      />
                    );
                  },
                );
              }
            })()
            : null}
        </div>
        {message.dealThreadMessage.content.files &&
          message.dealThreadMessage.content.files.length > 0 ? (
          <PillsWithShowMore>
            {message.dealThreadMessage.content.files.map((file) => {
              return (
                <FilePill
                  key={file.dataRoomFileId}
                  id={file.dataRoomFileId}
                  name={file.dataRoomFileName}
                  type={file.dataRoomFileType}
                  showDetailsCard={false}
                  pageIndex={file.pageIndex ? file.pageIndex : undefined}
                  rectsOnPage={file.rectsOnPage ? file.rectsOnPage : undefined}
                />
              );
            })}
          </PillsWithShowMore>
        ) : null}
      </div>
    </animated.div>
  );
}

function AiMessageQuoteGroup(props: {
  fileId: string;
  fileQuotes: DealThreadMessageContentQuote[];
}) {
  const { fileId, fileQuotes } = props;
  const { dealId } = useParams<{ dealId: string }>();

  const firstQuote = fileQuotes[0];
  const isVerified = fileQuotes.every(
    (quote) => quote.status === DealThreadMessageContentQuoteStatus.Verified,
  );
  const hasExcelData = fileQuotes.some((quote) => quote.excel?.csv);

  const [showQuotes, setShowQuotes] = useState(false);

  return (
    <div
      className="bg-white shadow border my-1 border-gray-200 rounded-md space-y-2 cursor-pointer hover:bg-concrete-50"
      key={`${fileId}-group`}
    >
      {/* Show file pill or question pill at the top */}
      <div
        className="px-2 flex items-start  pt-2 pb-1.5 gap-x-1.5 select-none"
        onClick={() => setShowQuotes(!showQuotes)}
      >
        {showQuotes ? (
          <ChevronDownIcon className="flex-shrink-0 w-4 h-4 text-concrete-500" />
        ) : (
          <ChevronRightIcon className="flex-shrink-0 w-4 h-4 text-concrete-500" />
        )}
        {fileId.startsWith("dtrmfile_") ? (
          <div className="flex gap-x-2 min-w-0">
            <FileIcon fileType={firstQuote.dataRoomFileType} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-concrete-600/90 leading-none truncate">
                {firstQuote.dataRoomFileName}
              </p>
              <p className="text-gray-500 text-xs">
                {fileQuotes.length} verified references
              </p>
            </div>
          </div>
        ) : fileId.startsWith("question_") ? (
          <Pills>
            {fileQuotes.map((quote, i) =>
              quote.questionId ? (
                <QuestionPill
                  key={`${fileId}-question-${i}`}
                  questionId={quote.questionId}
                  style="assistant"
                />
              ) : null,
            )}
          </Pills>
        ) : null}
      </div>

      {showQuotes ? (
        <div>
          {/* Render Excel snippet for verified quotes with Excel data */}
          {isVerified && hasExcelData ? (
            <div className="overflow-x-auto">
              <div className="flex space-x-2 pb-2">
                {fileQuotes.map((quote, i) =>
                  quote.excel?.csv ? (
                    <div
                      key={`${fileId}-excel-${i}`}
                      className="flex-shrink-0 w-2/3 max-w-80 p-3 bg-gray-50 border border-gray-200 rounded-md"
                    >
                      <ExcelSnippet
                        content={quote.excel?.csv ?? ""}
                        searchTerm=""
                        showMoreByDefault={true}
                      />
                    </div>
                  ) : null,
                )}
              </div>
            </div>
          ) : (
            /* Regular quote rendering for non-Excel or unverified quotes */
            <div className="overflow-x-auto scrollbar-none">
              <div className="flex space-x-2 px-2 pb-2">
                {fileQuotes.map((quote, i) => {
                  return (
                    <NavLink
                      key={`${fileId}-quote-${i}`}
                      to={links.fileLink(
                        quote.dataRoomFileId,
                        quote.dataRoomFolderId,
                        dealId,
                        {
                          pageIndex: quote.pageIndex,
                          rectsOnPage: quote.rectsOnPage,
                        },
                      )}
                      className=" flex-shrink-0 w-4/5 max-w-80 p-3 bg-gray-50 border border-gray-200 rounded-md hover:shadow-sm hover:border-gray-300"
                    >
                      <div>
                        <div className="flex items-center justify-between">
                          {quote.pageIndex !== undefined ? (
                            <p className="text-xs font-semibold text-concrete-800">
                              Page {quote.pageIndex + 1}
                            </p>
                          ) : null}
                          <ExternalLinkIcon className="text-gray-500 w-3 h-3" />
                        </div>
                        <p className="text-xs text-gray-600">
                          {quote.quote.slice(
                            0,
                            Math.min(quote.quote.length, 90),
                          )}
                          {quote.quote.length > 90 ? "..." : ""}
                        </p>
                      </div>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function AiMessageSources(props: { message: DealThreadMessageCreatedData }) {
  if (
    !props.message.dealThreadMessage.sources ||
    props.message.dealThreadMessage.sources.sources.length === 0
  ) {
    return null;
  }

  return (
    <div className="mb-4">
      <p className="text-xs font-semibold text-gray-600">Source data</p>
      <PillsWithShowMore>
        {(props.message.dealThreadMessage.sources.sources ?? []).map(
          (s, index) => {
            if (s.type === DealThreadMessageSourceType.DataRoomFile) {
              if (!s.file) {
                return null;
              }

              return (
                <FilePill
                  key={`${s.file.id}-${index}`}
                  showDetailsCard={false}
                  id={s.file.id}
                  name={s.file.name}
                  type={s.file.fileType}
                  hasVerifiedQuote={s.hasVerifiedQuote ?? false}
                />
              );
            }
            return null;
          },
        )}
      </PillsWithShowMore>
    </div>
  );
}


function AiMessageTextBlock(props: { block: { text?: string | null } }) {
  if (!props.block.text) {
    return null;
  }

  return (
    <div className="text-sm text-gray-600 break-words leading-relaxed">
      <AnswerViewer content={props.block.text} />
    </div>
  );
}

function AiMessageContentBlocks(props: {
  blocks: DealThreadMessageContentBlock[];
}) {
  const prefersReducedMotion = useReducedMotion();
  const grouped = groupBlocks(props.blocks as AiBlockFields[], DealThreadMessageContentBlockType.ToolCall);

  return (
    <div className="space-y-2 min-w-0 w-full">
      <AnimatePresence initial={false}>
        {grouped.map((group) => {
          if (group.kind === "toolGroup") {
            const key = `tool-group-${group.startIndex}`;
            return (
              <motion.div
                key={key}
                className="w-full min-w-0"
                layout
                initial={prefersReducedMotion ? undefined : { opacity: 0, y: 8 }}
                animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
                exit={prefersReducedMotion ? undefined : { opacity: 0, y: -6 }}
                transition={{
                  duration: prefersReducedMotion ? 0 : 0.2,
                  ease: [0.2, 0.8, 0.2, 1],
                }}
              >
                <ToolCallGroup blocks={group.blocks} followedByContent={group.followedByContent} />
              </motion.div>
            );
          }

          const block = group.block;
          const key = block.toolCallId ?? `${block.type}-${group.index}`;

          if (block.type === DealThreadMessageContentBlockType.Text) {
            return (
              <div key={key} className="w-full min-w-0">
                <AiMessageTextBlock block={block} />
              </div>
            );
          }

          if (block.type === DealThreadMessageContentBlockType.Reasoning) {
            return (
              <motion.div
                key={key}
                className="w-full min-w-0"
                layout
                initial={prefersReducedMotion ? undefined : { opacity: 0, y: 8 }}
                animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
                exit={prefersReducedMotion ? undefined : { opacity: 0, y: -6 }}
                transition={{
                  duration: prefersReducedMotion ? 0 : 0.2,
                  ease: [0.2, 0.8, 0.2, 1],
                }}
              >
                <AiMessageReasoningBlock reasoning={block.reasoning} durationMs={block.durationMs} />
              </motion.div>
            );
          }

          return null;
        })}
      </AnimatePresence>
    </div>
  );
}

function AiMessageContent(props: { message: DealThreadMessageCreatedData }) {
  const contentBlocks = props.message.dealThreadMessage.content.blocks;

  if (contentBlocks && contentBlocks.length > 0) {
    return <AiMessageContentBlocks blocks={contentBlocks} />;
  }

  return (
    <div className="text-sm text-gray-600 break-words leading-relaxed">
      <AnswerViewer
        content={props.message.dealThreadMessage.content.text.value}
      />
    </div>
  );
}

function QuoteStatus(props: { status: DealThreadMessageContentQuoteStatus }) {
  if (props.status === DealThreadMessageContentQuoteStatus.Unverified) {
    return (
      <div className="flex items-center gap-x-2">
        <FileWarningIcon className="w-3 h-3 text-gray-500" />
        <p className="text-sm font-semibold text-gray-600">
          Quote not verified yet
        </p>
      </div>
    );
  }

  if (props.status === DealThreadMessageContentQuoteStatus.Verifying) {
    return (
      <div className="flex items-center ">
        <Spinner color="gray" size="s" />
        <p className="text-sm text-gray-600 font-semibold">
          Verifying quote...
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-x-2">
      <CheckCircleIcon className="w-3 h-3 text-green-500" />
      <p className="text-sm text-gray-600 font-semibold">Quote verified</p>
    </div>
  );
}
